import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, LogOut } from 'lucide-react';
import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import { haptic } from '../utils/vibration';
import { DEFAULT_USER_AGREEMENT_DOCUMENT } from '../legal/userAgreement';
import UserAgreementDocument from './UserAgreementDocument';

const UserAgreementGate = ({ onAccepted, onLogout }) => {
  const [agreement, setAgreement] = useState(DEFAULT_USER_AGREEMENT_DOCUMENT);
  const [agreementLoaded, setAgreementLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let disposed = false;

    const loadAgreement = async () => {
      try {
        const document = await safeFetch(`${API_URL}/api/user-agreement`, {}, 12000);
        if (!disposed) {
          setAgreement(document);
          setAgreementLoaded(true);
        }
      } catch (loadError) {
        console.warn('Не удалось загрузить пользовательское соглашение:', loadError);
        if (!disposed) {
          setAgreementLoaded(true);
        }
      }
    };

    loadAgreement();
    return () => {
      disposed = true;
    };
  }, []);

  const handleAccept = async () => {
    haptic('selection');
    setLoading(true);
    setError('');

    try {
      const status = await safeFetch(`${API_URL}/api/user-agreement/accept`, {
        method: 'POST',
        body: JSON.stringify({ version: agreement.version })
      }, 12000);
      haptic('success');
      onAccepted?.(status);
    } catch (acceptError) {
      haptic('error');
      setError(acceptError.message || 'Не удалось сохранить согласие');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex min-h-[100dvh] flex-col bg-gray-50 text-gray-950 dark:bg-gray-950 dark:text-gray-50">
      <header className="shrink-0 border-b border-gray-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/95">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-base font-bold">Большая Стирка</div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">Перед началом работы примите соглашение</div>
          </div>
          <button
            type="button"
            onClick={() => {
              haptic('warning');
              onLogout?.();
            }}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-red-300"
            title="Выйти"
            aria-label="Выйти"
            disabled={loading}
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-4">
        <div className="mx-auto max-w-5xl">
          <UserAgreementDocument agreement={agreement} />
        </div>
      </main>

      <footer className="shrink-0 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur dark:border-gray-800 dark:bg-gray-900/95">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-5 text-gray-600 dark:text-gray-300">
            Нажимая "Согласен", вы подтверждаете принятие редакции {agreement.version}.
          </p>
          <div className="flex flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-950/30 dark:text-red-300">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={handleAccept}
              disabled={loading || !agreementLoaded}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              <span>{loading ? 'Сохраняю...' : agreementLoaded ? 'Согласен' : 'Загрузка...'}</span>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserAgreementGate;
